import { Check, ChevronRight, Copy } from "lucide-react";
import { useState } from "react";
import type { PipelineRun, PipelineStep } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatCost, formatElapsed } from "@/lib/format";
import { useStepArtifacts } from "@/lib/useStepArtifacts";
import { StepDetails } from "@/pages/StepDetail";

/**
 * ARCH-8 — the Debug tab, behind developer mode.
 *
 * "Per step settings, input, output, description", says the feature, "and here and there
 * add things a developer might like, like showing the guid identifier of a recording
 * with a copy button". So: one row per step of the run the Server reports — not a
 * hardcoded list of steps, because M4 adds three more and M5 more again — with what it
 * was given, what it produced and how long it took.
 *
 * Artifacts are fetched per step, when a step is opened, rather than with the run. Every
 * step of every recording carries at least an input, and a reconciled transcript is tens
 * of thousands of words; loading all of that to draw a list of four rows would make this
 * tab the slowest thing in the app.
 *
 * What a volume-backed artifact shows is its path and its size, never its body. That is
 * the honest amount: the question this tab answers is "what ran, on what, and how long
 * did it take", and the transcript itself has two screens of its own.
 */
export function DebugTab({ run, recordingId }: { run: PipelineRun; recordingId: string }) {
  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          This recording
        </h2>

        <dl className="mt-[var(--space-3)] grid grid-cols-[auto_1fr] items-center gap-x-[var(--space-4)] gap-y-2">
          <Identifier label="Recording" value={recordingId} />
          <Identifier label="Run" value={run.id} />
        </dl>

        <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {run.restartedFromStep
            ? `Restarted at ${run.restartedFromStep}. Everything from that step on was rebuilt.`
            : "This run has not been restarted at a step."}
        </p>
      </section>

      <ol className="flex flex-col gap-[var(--space-2)]">
        {run.steps.map((step) => (
          <StepRow key={step.id} step={step} recordingId={recordingId} />
        ))}
      </ol>
    </div>
  );
}

function StepRow({ step, recordingId }: { step: PipelineStep; recordingId: string }) {
  const [open, setOpen] = useState(false);
  const { artifacts, problem } = useStepArtifacts(recordingId, step.id, open);

  const elapsed =
    step.startedAt && step.completedAt
      ? Date.parse(step.completedAt) - Date.parse(step.startedAt)
      : null;

  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center gap-[var(--space-3)] px-[var(--space-4)] py-[var(--space-3)] text-left"
      >
        <ChevronRight
          size={14}
          strokeWidth={2}
          aria-hidden
          className={cn("shrink-0 text-[var(--text-muted)] transition-transform", open && "rotate-90")}
        />

        <span className="min-w-0 flex-1 truncate font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
          {step.stepName}
        </span>

        <span className="shrink-0 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {[
            step.status,
            elapsed !== null && elapsed >= 0 ? formatElapsed(elapsed) : null,
            // "on GPU-WORKSTATION-01", or on nothing: a step nothing has claimed has no
            // machine, and a machine whose row has since gone leaves the id behind.
            step.claimedByName,
            step.model,
            // Only a cloud step that actually called Anthropic has one to show.
            step.costUsd !== null ? formatCost(step.costUsd) : null,
            step.attempts > 1 ? `attempt ${step.attempts}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </button>

      {open && <StepDetails step={step} artifacts={artifacts} problem={problem} />}
    </li>
  );
}

/** A guid with a copy button, which is the "thing a developer might like" ARCH-8 names
 * outright. */
function Identifier({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard still shows the value, which is the part
      // that matters.
    }
  }

  return (
    <>
      <dt className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </dt>
      <dd className="flex items-center gap-[var(--space-2)]">
        <code className="truncate font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {value}
        </code>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={`Copy ${label.toLowerCase()} identifier`}
          className="shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
        >
          {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={2} />}
        </button>
      </dd>
    </>
  );
}

